import { useState, useEffect } from 'react';
import api from '@/api';
import { Settings, Plus, Save, RefreshCw } from 'lucide-react';

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '', client: '', project_type: '', discipline: 'all',
    header_synonyms: {}, room_number_rules: [], confidence_thresholds: { high: 0.9, medium: 0.75, low: 0.5 },
  });

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/templates');
      setTemplates(res.data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleCreate = async () => {
    try {
      await api.post('/templates', form);
      setShowForm(false);
      setForm({ name: '', client: '', project_type: '', discipline: 'all', header_synonyms: {}, room_number_rules: [], confidence_thresholds: { high: 0.9, medium: 0.75, low: 0.5 } });
      await fetchTemplates();
    } catch (err) {
      console.error('Create failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-4xl" data-testid="templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight uppercase text-foreground">Parsing Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure extraction rules and field mappings</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="new-template-btn"
          className="flex items-center gap-2 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-sm h-10 px-6 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Template
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-sm p-5 mb-6 animate-fade-in" data-testid="template-form">
          <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground mb-4">New Template</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Template Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="template-name-input"
                className="w-full bg-card border border-border focus:border-primary focus:ring-1 focus:ring-orange-500/50 rounded-sm px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                placeholder="e.g. Commercial Office Default"
              />
            </div>
            <div>
              <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Client</label>
              <input
                value={form.client}
                onChange={(e) => setForm({ ...form, client: e.target.value })}
                data-testid="template-client-input"
                className="w-full bg-card border border-border focus:border-primary focus:ring-1 focus:ring-orange-500/50 rounded-sm px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                placeholder="Optional client name"
              />
            </div>
            <div>
              <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Project Type</label>
              <input
                value={form.project_type}
                onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                data-testid="template-project-type-input"
                className="w-full bg-card border border-border focus:border-primary focus:ring-1 focus:ring-orange-500/50 rounded-sm px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                placeholder="e.g. commercial_office, healthcare"
              />
            </div>
            <div>
              <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Discipline</label>
              <select
                value={form.discipline}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })}
                data-testid="template-discipline-select"
                className="w-full bg-card border border-border focus:border-primary rounded-sm px-3 py-2 font-mono text-sm text-foreground outline-none transition-colors"
              >
                <option value="all">All</option>
                <option value="architectural">Architectural</option>
                <option value="mechanical">Mechanical</option>
                <option value="electrical">Electrical</option>
                <option value="plumbing">Plumbing</option>
              </select>
            </div>
          </div>
          <button
            onClick={handleCreate}
            disabled={!form.name}
            data-testid="save-template-btn"
            className="flex items-center gap-2 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-xs h-9 px-4 transition-colors disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" />
            Save Template
          </button>
        </div>
      )}

      {/* Template List */}
      <div className="space-y-3">
        {templates.map((t) => (
          <div key={t.id} className="bg-card border border-border rounded-sm p-5 hover:border-border transition-colors" data-testid={`template-${t.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">{t.name}</h3>
                <div className="flex items-center gap-4 mt-1">
                  {t.client && <span className="font-mono text-[10px] text-muted-foreground">Client: {t.client}</span>}
                  <span className="font-mono text-[10px] text-muted-foreground">Type: {t.project_type}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">Discipline: {t.discipline}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">v{t.version}</span>
                </div>
              </div>
              <Settings className="w-4 h-4 text-muted-foreground" />
            </div>
            {/* Confidence thresholds */}
            <div className="mt-3 flex gap-3">
              {Object.entries(t.confidence_thresholds || {}).map(([k, v]) => (
                <span key={k} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wide border ${
                  k === 'high' ? 'bg-emerald-950/30 text-emerald-400 border-emerald-900' :
                  k === 'medium' ? 'bg-amber-950/30 text-amber-400 border-amber-900' :
                  'bg-rose-950/30 text-rose-400 border-rose-900'
                }`}>
                  {k}: {Math.round(v * 100)}%
                </span>
              ))}
            </div>
          </div>
        ))}

        {templates.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            No templates yet. Create one to configure extraction rules.
          </div>
        )}
      </div>
    </div>
  );
}
